/**
 * Quanta Calculator - Mathematical Expression Evaluator
 * MATLAB-compatible syntax for scientific calculations
 */
// Re-export MATLAB implementation as the primary evaluator
export { MatlabTokenType as TokenType, MatlabLexerError as LexerError, MatlabParseError as ParseError, MatlabRuntimeError as RuntimeError, MatlabError, } from "./matlab/types";
export { MatlabLexer as Lexer, matlabTokenize as tokenize, } from "./matlab/lexer";
export { MatlabParser as Parser, matlabParse as parse } from "./matlab/parser";
export { MatlabEnvironment as Environment, MatlabEvaluator as Evaluator, matlabEvaluate as evaluate, isAsyncOperation, } from "./matlab/evaluator";
export { matlabBuiltins as builtinFunctions, matlabConstants as builtinConstants, } from "./matlab/builtins";
// MATLAB module access
export * as matlab from "./matlab";
export { matlabExecute, safeMatlabExecute, createMatlabEnvironment, isMatlabFile, parseMatlabFile, } from "./matlab";
// High-level API using MATLAB implementation
import { matlabParse } from "./matlab/parser";
import { matlabEvaluate, hoistFunctionDeclarations, MatlabEnvironment, } from "./matlab/evaluator";
import { isPlotValue, isPlotMetadata, applyPlotMetadata } from "./matlab/index";
import { MatlabError } from "./matlab/types";
// fprintf and disp write to the transcript in MATLAB even when the call ends
// in a semicolon, which is how a script reports its numbers.
function isPrintingCall(node) {
    return (node.type === "FunctionCall" &&
        ["fprintf", "disp"].includes(node.name));
}
// What a printing call contributes to the transcript. The trailing newline is
// already implied by joining the statement outputs.
function printedText(value) {
    return formatValue(value).replace(/\n+$/, "");
}
/**
 * Get the name of an assignment target for display
 */
function getAssignmentName(node) {
    if (node.type === "Assignment") {
        if (node.target?.type === "Identifier") {
            return node.target.name;
        }
        if (node.target?.type === "IndexAccess" &&
            node.target.object?.type === "Identifier") {
            return node.target.object.name;
        }
    }
    if (node.type === "MultiAssignment" && node.targets) {
        return "[" + node.targets.map((t) => t.name || "~").join(", ") + "]";
    }
    return null;
}
/**
 * Execute a mathematical expression and return the result
 */
export function execute(code, env) {
    const ast = matlabParse(code);
    const execEnv = env || new MatlabEnvironment();
    // Handle multi-statement blocks: collect output for each statement (MATLAB style)
    if (ast.type === "Block" && Array.isArray(ast.statements)) {
        const statements = ast.statements;
        // Local functions are usually written below the code that calls them.
        hoistFunctionDeclarations(statements, execEnv);
        const outputs = [];
        const latexOutputs = [];
        let lastValue = null;
        let lastType = "null";
        let lastPlot = null;
        const allPlots = [];
        // Ordered segments so the UI can interleave text and plots as produced.
        const segments = [];
        let textBuf = [];
        let latexBuf = [];
        const flushText = () => {
            if (textBuf.length > 0) {
                segments.push({
                    text: textBuf.join("\n"),
                    latex: latexBuf.join(" \\\\ "),
                });
                textBuf = [];
                latexBuf = [];
            }
        };
        // Split source into lines for error reporting
        const sourceLines = code.split("\n");
        for (let si = 0; si < statements.length; si++) {
            const stmt = statements[si];
            let value;
            try {
                value = matlabEvaluate(stmt, execEnv);
            }
            catch (error) {
                // Enhance error with line information
                const stmtLine = stmt.sourceLine;
                const lineText = stmtLine != null ? sourceLines[stmtLine - 1]?.trim() : undefined;
                const baseMsg = error instanceof Error ? error.message : String(error);
                const lineInfo = stmtLine != null
                    ? `Line ${stmtLine}${lineText ? `: ${lineText}` : ""}`
                    : `Statement ${si + 1}`;
                const enhanced = new MatlabError(`${baseMsg}\n  at ${lineInfo}`);
                throw enhanced;
            }
            const name = getAssignmentName(stmt);
            const suppress = stmt.suppressOutput === true;
            // A declaration is not a result, so it neither displays nor becomes the
            // value of the script.
            if (stmt.type === "FunctionDeclaration")
                continue;
            // Check if this is a plot metadata command that should modify the last
            // plot. Absorb it either way: a decoration command is not a result.
            if (isPlotMetadata(value)) {
                if (lastPlot) {
                    applyPlotMetadata(lastPlot, value);
                }
                continue; // Don't add metadata commands to output
            }
            if (isPlotValue(value)) {
                lastPlot = value;
                allPlots.push(value);
                // Emit the plot as its own segment at this point in the sequence, after
                // any text produced before it. Plots never contribute text output.
                flushText();
                segments.push({ plot: value });
                lastValue = value;
                lastType = getValueType(value);
                continue;
            }
            // Semicolons suppress output display (MATLAB convention), except for
            // the printing calls.
            if (isPrintingCall(stmt)) {
                // Printed text takes its place in the interleaved segments too, so a
                // script's diagnostics appear above the plot they describe.
                const printed = printedText(value);
                outputs.push(printed);
                textBuf.push(printed);
            }
            else if (!suppress) {
                if (name) {
                    // Assignment: show "name = value"
                    outputs.push(`${name} = ${formatValue(value)}`);
                    latexOutputs.push(`${name} = ${toLatex(value)}`);
                    textBuf.push(`${name} = ${formatValue(value)}`);
                    latexBuf.push(`${name} = ${toLatex(value)}`);
                }
                else {
                    // Expression: show just value
                    outputs.push(formatValue(value));
                    latexOutputs.push(toLatex(value));
                    textBuf.push(formatValue(value));
                    latexBuf.push(toLatex(value));
                }
            }
            lastValue = value;
            lastType = getValueType(value);
        }
        flushText();
        return {
            value: lastValue,
            display: outputs.join("\n"),
            type: lastType,
            latex: latexOutputs.join(" \\\\ "),
            // Surface every plot produced in the block (even a single one, and even
            // when the plot is not the block's final statement) so the UI can render
            // each. Without this, plots in multi-statement scripts get dropped.
            plots: allPlots.length > 0 ? allPlots : undefined,
            // Ordered text/plot segments for interleaved rendering.
            segments: segments.length > 0 ? segments : undefined,
        };
    }
    // Single statement
    const result = matlabEvaluate(ast, execEnv);
    const name = getAssignmentName(ast);
    if (name) {
        return {
            value: result,
            display: `${name} = ${formatValue(result)}`,
            type: getValueType(result),
            latex: `${name} = ${toLatex(result)}`,
        };
    }
    if (isPrintingCall(ast)) {
        return {
            value: result,
            display: printedText(result),
            type: getValueType(result),
            latex: "",
        };
    }
    return {
        value: result,
        display: formatValue(result),
        type: getValueType(result),
        latex: toLatex(result),
    };
}
/**
 * Create a new calculation environment
 */
export function createEnvironment() {
    return new MatlabEnvironment();
}
/**
 * Safely execute code and catch errors
 */
export function safeExecute(code, env) {
    try {
        const result = execute(code, env);
        return { success: true, result };
    }
    catch (error) {
        if (error instanceof MatlabError) {
            return { success: false, error };
        }
        return {
            success: false,
            error: new MatlabError(error instanceof Error ? error.message : String(error)),
        };
    }
}
// Helper functions
// Compact number formatting for matrix/array display
function formatCompactNum(value) {
    if (!Number.isFinite(value)) {
        return value > 0 ? "∞" : value < 0 ? "-∞" : "NaN";
    }
    if (value === 0)
        return "0";
    if (Number.isInteger(value) && Math.abs(value) < 1e6)
        return value.toString();
    // Use fewer digits for compact display
    const abs = Math.abs(value);
    if (abs >= 0.001 && abs < 1e6) {
        // Fixed notation with up to 4 decimal places
        const formatted = value.toFixed(4);
        // Remove trailing zeros
        return parseFloat(formatted).toString();
    }
    // Scientific notation for very large/small numbers
    return value.toExponential(3);
}
// Format any value compactly (used for array/matrix elements)
function formatCompact(value) {
    if (typeof value === "number")
        return formatCompactNum(value);
    return formatValue(value);
}
// Compact LaTeX formatting for array elements
function toLatexCompact(value) {
    if (typeof value === "number") {
        if (!Number.isFinite(value)) {
            return value > 0 ? "\\infty" : value < 0 ? "-\\infty" : "\\text{NaN}";
        }
        return formatCompactNum(value);
    }
    return toLatex(value);
}
// Maximum elements/rows to display before truncating
const MAX_DISPLAY_ELEMENTS = 10;
const MAX_DISPLAY_ROWS = 10;
const TRUNCATE_SHOW_FIRST = 4;
const TRUNCATE_SHOW_LAST = 2;
// Helper to right-pad strings for column alignment
function padColumn(str, width) {
    return str.padStart(width);
}
// Type guard for complex
function isComplex(value) {
    return (typeof value === "object" &&
        value !== null &&
        "re" in value &&
        "im" in value);
}
// Type guard for array
function isArray(value) {
    return Array.isArray(value);
}
// Type guard for matrix
function isMatrix(value) {
    return Array.isArray(value) && value.length > 0 && Array.isArray(value[0]);
}
/**
 * Format value for display
 */
export function formatValue(value) {
    if (value === null || value === undefined)
        return "";
    if (typeof value === "number") {
        if (Number.isInteger(value))
            return String(value);
        return value.toPrecision(10).replace(/\.?0+$/, "");
    }
    if (typeof value === "string")
        return value;
    // Complex number
    if (isComplex(value)) {
        const re = value.re;
        const im = value.im;
        if (im === 0)
            return formatValue(re);
        if (re === 0) {
            if (im === 1)
                return "i";
            if (im === -1)
                return "-i";
            return `${formatValue(im)}i`;
        }
        const imAbs = Math.abs(im);
        const sign = im >= 0 ? " + " : " - ";
        const imStr = imAbs === 1 ? "" : formatValue(imAbs);
        return `${formatValue(re)}${sign}${imStr}i`;
    }
    // Matrix (2D array)
    if (isMatrix(value)) {
        const rows = value.length;
        const cols = value[0].length;
        // Calculate column widths
        const colWidths = [];
        for (let c = 0; c < cols; c++) {
            let maxWidth = 0;
            for (let r = 0; r < Math.min(rows, MAX_DISPLAY_ROWS); r++) {
                const formatted = formatCompact(value[r][c]);
                maxWidth = Math.max(maxWidth, formatted.length);
            }
            colWidths.push(maxWidth);
        }
        const formatRow = (row) => {
            return row
                .map((v, c) => padColumn(formatCompact(v), colWidths[c]))
                .join("  ");
        };
        const header = `  ${rows}×${cols} matrix\n`;
        if (rows <= MAX_DISPLAY_ROWS) {
            const rowStrs = value.map((r) => formatRow(r));
            return header + rowStrs.map((r) => "    " + r).join("\n");
        }
        // Truncate
        const firstRows = value
            .slice(0, TRUNCATE_SHOW_FIRST)
            .map((r) => formatRow(r));
        const lastRows = value
            .slice(-TRUNCATE_SHOW_LAST)
            .map((r) => formatRow(r));
        const dotsRow = colWidths.map((w) => padColumn("⋮", w)).join("  ");
        return (header +
            firstRows.map((r) => "    " + r).join("\n") +
            "\n" +
            "    " +
            dotsRow +
            "\n" +
            lastRows.map((r) => "    " + r).join("\n"));
    }
    // 1D Array
    if (isArray(value)) {
        if (value.length === 0)
            return "[]";
        if (value.length <= MAX_DISPLAY_ELEMENTS) {
            return "[" + value.map((v) => formatCompact(v)).join(", ") + "]";
        }
        // Truncate
        const first = value
            .slice(0, TRUNCATE_SHOW_FIRST)
            .map((v) => formatCompact(v));
        const last = value.slice(-TRUNCATE_SHOW_LAST).map((v) => formatCompact(v));
        return `[${first.join(", ")} ... ${last.join(", ")}]  (${value.length} elements)`;
    }
    // Plot data
    if (isPlotValue(value)) {
        return "[Plot Data]";
    }
    // Function types
    if (typeof value === "object" && "type" in value) {
        const v = value;
        if (v.type === "function" && v.params)
            return `@${v.params.join(",")}`;
        if (v.type === "anonymous" && v.params)
            return `@(${v.params.join(",")})`;
        if (v.type === "handle" && v.name)
            return `@${v.name}`;
    }
    // Object/struct
    if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
}
/**
 * Get value type string
 */
export function getValueType(value) {
    if (value === null || value === undefined)
        return "null";
    if (typeof value === "number")
        return "number";
    if (typeof value === "string")
        return "string";
    if (isComplex(value))
        return "complex";
    if (isPlotValue(value))
        return "plot";
    if (isMatrix(value))
        return "matrix";
    if (isArray(value))
        return "array";
    if (typeof value === "object" && "type" in value) {
        return value.type;
    }
    return "object";
}
/**
 * Convert value to LaTeX string
 */
export function toLatex(value) {
    if (value === null || value === undefined)
        return "";
    if (typeof value === "number") {
        if (!Number.isFinite(value)) {
            return value > 0 ? "\\infty" : value < 0 ? "-\\infty" : "\\text{NaN}";
        }
        if (Number.isInteger(value))
            return String(value);
        return value.toPrecision(10).replace(/\.?0+$/, "");
    }
    if (typeof value === "string")
        return `\\text{${value}}`;
    // Complex number
    if (isComplex(value)) {
        const re = value.re;
        const im = value.im;
        if (im === 0)
            return toLatex(re);
        if (re === 0) {
            if (im === 1)
                return "i";
            if (im === -1)
                return "-i";
            return `${toLatex(im)}i`;
        }
        const sign = im >= 0 ? "+" : "-";
        const imStr = Math.abs(im) === 1 ? "" : toLatex(Math.abs(im));
        return `${toLatex(re)} ${sign} ${imStr}i`;
    }
    // Matrix
    if (isMatrix(value)) {
        const rows = value.map((row) => row.map((v) => toLatexCompact(v)).join(" & "));
        return `\\begin{bmatrix} ${rows.join(" \\\\ ")} \\end{bmatrix}`;
    }
    // Array
    if (isArray(value)) {
        if (value.length === 0)
            return "[]";
        return `\\begin{bmatrix} ${value.map((v) => toLatexCompact(v)).join(" & ")} \\end{bmatrix}`;
    }
    // Plot data
    if (isPlotValue(value)) {
        return "\\text{[Plot]}";
    }
    return String(value);
}
//# sourceMappingURL=index.js.map